import React, { useState } from "react";
import { ChevronDown, ChevronUp, Check, X } from "lucide-react";

export function FeatureComparisonTable({ plans, t }) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Filter to the 3 main visible plans
    const visiblePlans = plans.filter(
        (plan) =>
            !plan.hidden &&
            plan.name !== "Build with You" &&
            plan.name !== "Enterprise Plus"
    );

    // All features to compare
    const featureCategories = [
        {
            name: t("billing.comparison.basics") || "Basics",
            features: [
                { key: "removeAgentUIBadge", label: t("billing.planFeatures.removeAgentUIBadge") || "Remove AgentUI Badge" },
                { key: "unlimitedApps", label: t("billing.planFeatures.unlimitedApps") || "Unlimited apps" },
                { key: "unlimitedExternalUsers", label: t("billing.planFeatures.unlimitedExternalUsers") || "Unlimited external users" },
            ],
        },
        {
            name: t("billing.comparison.development") || "Development",
            features: [
                { key: "inAppCodeEdits", label: t("billing.planFeatures.inAppCodeEdits") || "In-app code edits" },
                { key: "backendFunctions", label: t("billing.planFeatures.backendFunctions") || "Backend functions" },
                { key: "standardIntegrations", label: t("billing.planFeatures.standardIntegrations") || "Standard integrations" },
                { key: "sqlIntegration", label: t("billing.planFeatures.sqlIntegration") || "SQL integration" },
            ],
        },
        {
            name: t("billing.comparison.team") || "Team & Security",
            features: [
                { key: "internalUsers", label: t("billing.comparison.internalUsers") || "Internal users", isNumeric: true, numericKey: "numberOfUsers" },
                { key: "environments", label: t("billing.comparison.environments") || "Environments", isNumeric: true, numericKey: "numberOfEnviroments" },
                { key: "customDomains", label: t("billing.planFeatures.connectDomain") || "Custom domain" },
                { key: "sharedSecrets", label: t("billing.planFeatures.sharedSecrets") || "Shared secrets" },
                { key: "internalCompanyPortal", label: t("billing.planFeatures.internalCompanyPortal") || "Internal portal" },
            ],
        },
        {
            name: t("billing.comparison.support") || "Support",
            features: [
                { key: "emailSupport", label: t("billing.planFeatures.emailSupport") || "Email support" },
                { key: "premiumSupport", label: t("billing.planFeatures.premiumSupport") || "Premium support" },
            ],
        },
    ];

    const hasFeature = (plan, featureKey) => {
        // Check enabled features
        if (plan.enabledFeatures?.[featureKey]) return true;
        // Check feature keys
        if (plan.featureKeys?.includes(featureKey)) return true;
        // Check features array for text match
        return plan.features?.some((f) =>
            f.toLowerCase().includes(featureKey.replace(/([A-Z])/g, " $1").toLowerCase())
        );
    };

    const getNumericValue = (plan, key) => {
        const value = plan[key];
        if (!value) return "-";
        const num = parseInt(value);
        if (num >= 999999) return t("billing.unlimited") || "Unlimited";
        return num;
    };

    return (
        <div className="mt-12">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-center gap-2 py-4 text-slate-600 hover:text-slate-900 transition-colors group"
            >
                <span className="text-sm font-medium">
                    {isExpanded
                        ? t("billing.hideFeatures") || "Hide full comparison"
                        : t("billing.compareAllFeatures") || "Compare all features"}
                </span>
                {isExpanded ? (
                    <ChevronUp className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                ) : (
                    <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                )}
            </button>

            {isExpanded && (
                <div className="mt-4 overflow-x-auto animate-in slide-in-from-top-2 duration-300">
                    <table className="w-full min-w-[600px]">
                        {/* Header */}
                        <thead>
                            <tr className="border-b border-slate-200">
                                <th className="text-left py-4 pr-4 text-sm font-medium text-slate-500 w-1/3">
                                    {t("billing.feature") || "Feature"}
                                </th>
                                {visiblePlans.map((plan) => (
                                    <th
                                        key={plan.name}
                                        className="text-center py-4 px-4 text-sm font-semibold text-slate-900"
                                    >
                                        {plan.nameKey
                                            ? t(`billing.planNames.${plan.nameKey}`, { defaultValue: plan.name })
                                            : plan.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {featureCategories.map((category) => (
                                <React.Fragment key={category.name}>
                                    {/* Category Header */}
                                    <tr className="bg-slate-50">
                                        <td
                                            colSpan={visiblePlans.length + 1}
                                            className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider"
                                        >
                                            {category.name}
                                        </td>
                                    </tr>
                                    {/* Features */}
                                    {category.features.map((feature) => (
                                        <tr key={feature.key} className="border-b border-slate-100">
                                            <td className="py-3 pr-4 text-sm text-slate-700">
                                                {feature.label}
                                            </td>
                                            {visiblePlans.map((plan) => (
                                                <td key={plan.name} className="text-center py-3 px-4">
                                                    {feature.isNumeric ? (
                                                        <span className="text-sm font-medium text-slate-700">
                                                            {getNumericValue(plan, feature.numericKey)}
                                                        </span>
                                                    ) : hasFeature(plan, feature.key) ? (
                                                        <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                                                    ) : (
                                                        <X className="w-5 h-5 text-slate-300 mx-auto" />
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
